import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { OEContext } from '@aytm/app/stats/page/comments'

import useResponseStatus from '@aytm/app/stats/page/comments/hooks/useResponseStatus'

import { REJECTED_OPTIONS } from '@aytm/app/stats/page/comments/lib/constants'

import { ListBox } from 'primereact/listbox'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'

import OverlayPanel from '@aytm/app/stats/components/overlay-panel/OverlayPanel'

import NotificationPopup from '@aytm/app/stats/page/comments/comments/components/Response/NotificationPopup'

import style from '@aytm/app/stats/page/comments/comments/styles/Response.module.scss'

const Reject = memo(
  ({
    responseId,
    rejectReason,
    setRejectReason,
    onChangeResponseStatus,
    onCloseNotification,
    popupNotificationsDisabled
  }) => {
    const { searchState } = useContext(OEContext)
    const { request, isAdmin } = searchState
    const { pageSize } = request

    const [otherReason, setOtherReason] = useState('')

    const rejectSelect = useRef(null)
    const rejectTrigger = useRef(null)
    const rejectTriggerText = useRef(null)
    // const rejectList = useRef(null)

    const isOnePerPage = useMemo(() => 1 == pageSize, [pageSize])

    const itemTemplate = option => <span dangerouslySetInnerHTML={{ __html: option.text }}></span>

    const onReject = useCallback(event => setRejectReason(event.value), [setRejectReason])

    // const onOpenRejectList = useCallback(event => {
    //   if (13 == event.keyCode || 32 == event.keyCode) {
    //     event.preventDefault()
    //     rejectSelect.current?.show(null, rejectTrigger.current)
    //   }
    // }, [])

    // a menu that never takes focus is mouse-only: ten rows a keyboard user cannot reach
    // const onShowRejectList = useCallback(() => {
    //   rejectList.current?.querySelector('.p-listbox-item')?.focus()
    // }, [])

    // const onHideRejectList = useCallback(() => {
    //   rejectTriggerText.current?.focus()
    // }, [])

    // const onRejectListKeyDown = useCallback(event => {
    //   if (27 == event.keyCode) rejectSelect.current?.hide()
    // }, [])

    const onConfirmReject = useCallback(() => {
      onChangeResponseStatus(responseId, 'reject', rejectReason)
      setRejectReason(rejectReason)
    }, [onChangeResponseStatus, responseId, rejectReason, setRejectReason])

    const onCancelReject = useCallback(() => {
      setRejectReason('')
    }, [setRejectReason])

    // closing the typed reason drops it too, so reopening Other starts from an empty field
    const onCancelOtherReason = useCallback(() => {
      setOtherReason('')
      setRejectReason('')
    }, [setRejectReason])

    const onChangeOtherReason = useCallback(event => {
      setOtherReason(event.target.value)
    }, [])

    const onOtherReject = useCallback(() => {
      if (otherReason) {
        onChangeResponseStatus(responseId, 'reject', otherReason)
        setRejectReason('')
      }
    }, [otherReason, onChangeResponseStatus, responseId, setRejectReason])

    const onRejectReasonKeyDown = useCallback(
      event => {
        if (27 == event.keyCode) {
          setOtherReason('')
          setRejectReason('')
          onCancelReject()
        }
        if (13 == event.keyCode) {
          // A typed "Other" reason submits that reason; otherwise confirm the
          // selected preset (incl. the literal `other`). Guarding with else-if
          // avoids firing both reject calls at once (see _open-questions.md §OQ-27).
          if (otherReason) {
            onOtherReject()
          } else if (rejectReason) {
            onConfirmReject()
          }
        }
      },
      [setRejectReason, onCancelReject, rejectReason, otherReason, onConfirmReject, onOtherReject]
    )

    const { status, isQAMode, qaAction, qaProcessedResponseId } = useResponseStatus(
      OEContext,
      responseId,
      'reject',
      event => {
        if (
          isOnePerPage &&
          (51 == event.keyCode || 99 == event.keyCode || 34 == event.keyCode) &&
          event.shiftKey &&
          rejectSelect.current &&
          // the panel needs an explicit anchor, and a mounted trigger is also what
          // distinguishes the card showing the affordance from the buffered ones
          rejectTrigger.current
        ) {
          rejectSelect.current.show(null, rejectTrigger.current)
        }
      }
    )

    // The preset confirmation's buttons hold no focus, so its keys are taken on the page. The Other field
    // takes its own keys, and a key typed in any field belongs to that field's card: listened to here as
    // well, one Enter sent twice, and a card left listening sent again for a key typed on another card.
    const onConfirmKeyDown = useCallback(
      event => {
        if (event.target?.closest?.('input, textarea, [contenteditable="true"]')) return
        onRejectReasonKeyDown(event)
      },
      [onRejectReasonKeyDown]
    )

    useEffect(() => {
      if (!rejectReason || 'other' == rejectReason) return
      window.addEventListener('keydown', onConfirmKeyDown)
      return () => {
        window.removeEventListener('keydown', onConfirmKeyDown)
      }
    }, [rejectReason, onConfirmKeyDown])

    switch (true) {
      case 'true' == status && !(isQAMode && isOnePerPage):
        return <>Rejected</>
      case 'loading' == status ||
        (isQAMode && 'reject' == qaAction && ('true' == status || qaProcessedResponseId == responseId)):
        return <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
      case 'object' == typeof status && 'error' == status.type:
        return <>Error</>
      default: {
        switch (true) {
          case 'other' == rejectReason: {
            return (
              <div className={style['reject-reason']}>
                <span onClick={onCancelOtherReason}>
                  <i className="icon-aytmfd-x-v2" />
                </span>
                <InputText
                  className="p-composite p-inputtext-sm"
                  placeholder="Please type a reason for rejection..."
                  style={{ width: '250px' }}
                  value={otherReason}
                  autoFocus
                  onKeyDown={onRejectReasonKeyDown}
                  onChange={onChangeOtherReason}
                />
                <Button
                  className="p-button-sm p-button-blue"
                  label="Reject"
                  disabled={!otherReason}
                  onClick={onOtherReject}
                />
              </div>
            )
          }
          case Boolean(rejectReason): {
            return (
              <div className={classNames(style['response-header-confirmation'], style['reject-confirmation'])}>
                <Button
                  className="p-button-sm p-button-blue p-button-secondary"
                  label="Cancel"
                  onClick={onCancelReject}
                />
                <Button
                  className={`${style['reject-confirm']} p-button-sm p-button-blue`}
                  label="Reject"
                  onClick={onConfirmReject}
                />
              </div>
            )
          }
          default:
            return (
              <div
                ref={rejectTrigger}
                className={classNames(style['response-header-reject'], {
                  [style['has-notification']]: !popupNotificationsDisabled['reject'] && !isAdmin
                })}
              >
                <OverlayPanel
                  ref={rejectSelect}
                  // onShow={onShowRejectList}
                  // onHide={onHideRejectList}
                  button={
                    <NotificationPopup
                      type="reject"
                      action="reject"
                      status="no"
                      disabled={popupNotificationsDisabled['reject'] || isAdmin}
                      onClosePopup={onCloseNotification}
                    >
                      <span
                        ref={rejectTriggerText}
                        className={style['response-header-reject-text']}
                        // onKeyDown={onOpenRejectList}
                      >
                        Reject <span className="p-c pi pi-chevron-down" />
                      </span>
                    </NotificationPopup>
                  }
                >
                  {/* <div ref={rejectList} onKeyDown={onRejectListKeyDown}> */}
                  <ListBox
                    className="p-listbox-no-scroll"
                    aria-label="Reject reason"
                    options={REJECTED_OPTIONS}
                    optionValue="value"
                    optionLabel="text"
                    itemTemplate={itemTemplate}
                    onChange={onReject}
                  />
                  {/* </div> */}
                </OverlayPanel>
              </div>
            )
        }
      }
    }
  }
)

Reject.propTypes = {
  popupNotificationsDisabled: PropTypes.object,
  onCloseNotification: PropTypes.func,
  response: PropTypes.object,
  responseId: PropTypes.number,
  rejectReason: PropTypes.string,
  setRejectReason: PropTypes.func,
  isRejected: PropTypes.bool,
  onChangeResponseStatus: PropTypes.func
}

Reject.displayName = 'Reject'

export default Reject
